export interface LargestContentfulPaint extends PerformanceEntry {
  entryType: 'largest-contentful-paint';
  element: Element | null;
  renderTime: DOMHighResTimeStamp;
  loadTime: DOMHighResTimeStamp;
  size: number;
  id: string;
  url: string;
}

export interface LayoutShiftAttribution {
  node: Node | null;
  previousRect: DOMRectReadOnly;
  currentRect: DOMRectReadOnly;
  toJSON(): string;
}

export interface LayoutShift extends PerformanceEntry {
  entryType: 'layout-shift';
  value: number;
  hadRecentInput: boolean;
  lastInputTime: DOMHighResTimeStamp;
  sources: LayoutShiftAttribution[];
}

export interface PerformanceElementTiming extends PerformanceEntry {
  entryType: 'element';
  element: Element | null;
  id: string;
  identifier: string;
  intersectionRect: DOMRectReadOnly;
  loadTime: number;
  naturalHeight: number;
  naturalWidth: number;
  renderTime: DOMHighResTimeStamp;
  url: string;
}

export interface PerformanceScriptTiming extends PerformanceEntry {
  entryType: 'script';
  executionStart: DOMHighResTimeStamp;
  forcedStyleAndLayoutDuration: DOMHighResTimeStamp;
  invoker: string;
  invokerType: string;
  pauseDuration: DOMHighResTimeStamp;
  sourceCharPosition: number;
  sourceFunctionName: string;
  sourceURL: string;
  window: Window;
  windowAttribution: 'ancestor' | 'descendant' | 'other' | 'same-page' | 'self';
}

export interface PerformanceLongAnimationFrameTiming extends PerformanceEntry {
  entryType: 'long-animation-frame';
  blockingDuration: DOMHighResTimeStamp;
  firstUIEventTimestamp: DOMHighResTimeStamp;
  renderStart: DOMHighResTimeStamp;
  scripts: PerformanceScriptTiming[];
  styleAndLayoutStart: DOMHighResTimeStamp;
}

export interface TaskAttributionTiming extends PerformanceEntry {
  entryType: 'taskattribution';
  containerType: 'iframe' | 'embed' | 'object';
  containerSrc: string;
  containerId: string;
  containerName: string;
}

export interface PerformanceLongTaskTiming extends PerformanceEntry {
  entryType: 'longtask';
  attribution: TaskAttributionTiming[];
}

export interface PerformanceResourceTiming
  extends globalThis.PerformanceResourceTiming {
  renderBlockingStatus: 'blocking' | 'non-blocking' | null;
}

export interface PerformanceNavigationTiming
  extends PerformanceResourceTiming,
    // eslint-disable-next-line compat/compat
    globalThis.PerformanceNavigationTiming {}

export interface VisibilityStateEntry extends PerformanceEntry {
  entryType: 'visibilitystate';
  name: 'hidden' | 'visible';
}

export interface PerformanceEventTiming
  // eslint-disable-next-line compat/compat
  extends globalThis.PerformanceEventTiming {
  interactionId: number;
}

export type PerformanceEntryTypeMap = {
  element: PerformanceElementTiming;
  event: PerformanceEventTiming;
  'first-input': PerformanceEventTiming;
  'largest-contentful-paint': LargestContentfulPaint;
  'layout-shift': LayoutShift;
  'long-animation-frame': PerformanceLongAnimationFrameTiming;
  longtask: PerformanceLongTaskTiming;
  mark: PerformanceMark;
  measure: PerformanceMeasure;
  navigation: PerformanceNavigationTiming;
  paint: PerformancePaintTiming;
  resource: PerformanceResourceTiming;
  taskattribution: TaskAttributionTiming;
  'visibility-state': VisibilityStateEntry;
};

export type PerformanceEntryType = keyof PerformanceEntryTypeMap;

/**
 * Type guard to check if a performance entry is of a specific type
 */
export function isPerformanceEntryOfType<T extends PerformanceEntryType>(
  entry: PerformanceEntry,
  type: T
): entry is PerformanceEntryTypeMap[T] {
  return entry.entryType === type;
}

/**
 * Check if a performance entry type is supported by the browser
 */
export function isPerformanceEntryTypeSupported(
  type: PerformanceEntryType
): boolean {
  // We need to ignore compat/compat here as we're checking for support
  /* eslint-disable compat/compat */
  return (
    typeof PerformanceObserver !== 'undefined' &&
    PerformanceObserver.supportedEntryTypes &&
    PerformanceObserver.supportedEntryTypes.includes(type)
  );
  /* eslint-enable compat/compat */
}
